import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setParent as updateParent } from "../../redux/userSlice";
import { calculateAge } from "../../lib/utils";
import { continueButtonSx, inputLabelSx, textAreaSx } from "../../lib/uiformatting";
import { Box, TextField, FormControl, Typography, useMediaQuery, useTheme } from "@mui/material";
import Question from "../../components/Question";
import MoveButton from "../../components/MoveButton";
import ContinueButton from "../../components/ContinueButton";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

export default function MinorConsent() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { returning = false } = location.state || {};
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const textAlignment = isDesktop ? "center" : "left";

    const user = useSelector((state) => state.user);
    const [parent, setParent] = useState(user.parent || { name: "", relationship: "" });
    const [nameError, setNameError] = useState("");
    const [relError, setRelError] = useState("");
    const [age, setAge] = useState(null);

    const relationshipOptions = {
        Parent: "parent",
        Guardian: "guardian",
        Other: "other",
    };

    useEffect(() => {
        setAge(calculateAge(user?.dob));
    }, [user]);

    const handleNameChange = useCallback((event) => {
        const value = event.target.value;
        setParent((prev) => ({ ...prev, name: value }));

        if (!value) {
            setNameError("This field is required.");
        } else if (!/.+ .+/.test(value)) {
            setNameError("Please enter both your first and last name.");
        } else {
            setNameError("");
        }
    }, []);

    const handleRelationshipChange = useCallback((value) => {
        setParent((prev) => ({ ...prev, relationship: value }));
        setRelError(value === "");
    }, []);

    const handleNext = useCallback(() => {
        dispatch(updateParent(parent));
        if (age > 14 && age < 18) {
            setPageAndDeferNavigate(dispatch, '/minor/solo-treatment', () =>
                navigate('/minor/solo-treatment', { state: { returning } })
            );
        } else {
            setPageAndDeferNavigate(dispatch, '/minor/weight', () =>
                navigate('/minor/weight', { state: { returning } })
            );
        }
    }, [dispatch, parent, age, navigate]);

    const handleBack = () => {
        if (returning) {
            setPageAndDeferNavigate(dispatch, '/patient-cases', () =>
                navigate('/patient-cases', { state: { returning: returning } })
            );
        } else {
            setPageAndDeferNavigate(dispatch, '/demographic', () =>
                navigate('/demographic', { state: { returning: returning } })
            );
        }
    };

    return (
        <Question
            onBack={handleBack}
            title="To treat patients under 18, we require the consent of a parent or guardian."
            input={
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: textAlignment, width: "100%" }}>

                    <FormControl sx={{ width: "100%", maxWidth: "600px" }} required>
                        <TextField
                            label="Full name of parent or guardian "
                            variant="outlined"
                            color="secondary"
                            multiline
                            rows={2}
                            sx={{
                                ...textAreaSx, width: "100%",
                                fontSize: "12px",
                                "& .MuiInputLabel-root": {
                                    backgroundColor: theme.palette.background.paper,
                                    padding: "0 32px",
                                    transition: "all 0.2s ease-in-out",
                                },
                            }}
                            InputLabelProps={{
                                sx: {
                                    ...inputLabelSx,
                                    fontSize: "12px",
                                    "& .MuiInputLabel-shrink": {
                                        backgroundColor: theme.palette.background.paper,
                                    },
                                }
                            }}
                            autoComplete="off"
                            value={parent.name}
                            onChange={handleNameChange}
                            error={Boolean(nameError)}
                            helperText={nameError}
                        />
                    </FormControl>

                    <Typography variant="h5" sx={{
                        mt: "1.5rem", mb: "1rem", textAlign: textAlignment, fontFamily: "Sentient", fontSize: "clamp(14px, 3vh, 28px)",
                        fontWeight: 400,
                    }}>
                        Relationship to patient
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            width: "100%",
                            maxWidth: "600px",
                            gap: "1rem",
                        }}
                    >
                        {Object.entries(relationshipOptions).map(([label, value]) => (
                            <MoveButton
                                key={value}
                                onClick={() => handleRelationshipChange(value)}
                                title={label}
                                selected={parent.relationship === value}
                            />
                        ))}
                    </Box>
                </Box>
            }
            footer={
                <Box
                    sx={{ ...continueButtonSx }}
                >
                    <ContinueButton
                        onClick={handleNext}
                        title="Continue"
                        disabled={!parent.name || !parent.relationship}
                        selected={Boolean(parent.name) && Boolean(parent.relationship)}
                    />
                </Box>
            }
        />
    );
}